import React from "react";
import { ShieldCheck } from "lucide-react";
import { FONT } from "@/lib/tokens";

/* ──────────────────────────────────────────────────────────────
   Le panneau des outils, en HTML natif.

   Pendant droit de SystemStackDiagram : meme raison d'etre, meme
   traitement. Le texte n'est plus grave dans une image, donc il
   reste net, indexable, et il suit les tokens du site.

   Seuls les logos de marques restent des images — Make, n8n et
   Zapier ne se redessinent pas. Le reste (tableur, CRM, IA, API)
   ce sont des CATEGORIES, pas des marques : du texte, sur une
   tuile plus discrete. Cette distinction evite de laisser croire
   qu'il existe un produit appele « IA », et elle n'engage TANGIG
   sur aucun fournisseur precis.

   Regle de fond : ces noms sont cites au CONDITIONNEL. Ce ne sont
   pas des integrations fournies par TANGIG, ce sont les briques
   qu'un systeme peut utiliser.
   ────────────────────────────────────────────────────────────── */

const INK    = "#1D1D1F";
const GREY_1 = "#6E6E73";
const LINE   = "#E8E8ED";
/* Le teal des accents, releve sur la maquette d'origine. */
const TEAL   = "#00716F";

const CSS = `
  .tg-tools {
    font-family: ${FONT};
    display: flex;
    flex-direction: column;
    gap: 28px;
    width: 100%;
  }

  /* ── La carte des technologies ── */
  .tg-tools-card {
    background: #FFFFFF;
    border: 1px solid ${LINE};
    border-radius: 22px;
    padding: 26px 26px 24px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03), 0 14px 34px -20px rgba(0,0,0,0.16);
    box-sizing: border-box;
  }

  .tg-tools-intro {
    font-size: 15px;
    line-height: 1.5;
    letter-spacing: -0.01em;
    color: ${INK};
    margin: 0 0 18px;
  }

  .tg-tools-row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 10px;
  }

  .tg-tools-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 46px;
    padding: 0 16px;
    background: #FFFFFF;
    border: 1px solid ${LINE};
    border-radius: 11px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03), 0 6px 14px -10px rgba(0,0,0,0.18);
    box-sizing: border-box;
  }

  .tg-tools-chip img { display: block; height: 22px; width: auto; }

  /* Les categories ne sont pas des marques : elles restent du texte,
     sur une tuile volontairement plus discrete. */
  .tg-tools-chip.is-kind {
    background: #F5F5F7;
    box-shadow: none;
    font-size: 14.5px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${GREY_1};
  }

  /* ── L'explication pour le non-technicien ── */
  .tg-tools-explain {
    font-size: 15px;
    line-height: 1.65;
    letter-spacing: -0.01em;
    color: ${GREY_1};
    margin: 0;
    padding: 0 4px;
  }

  /* ── La promesse ── */
  .tg-tools-claim {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    background: #FFFFFF;
    border: 1px solid rgba(0,113,111,0.35);
    border-radius: 22px;
    padding: 28px 28px 26px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03), 0 14px 34px -20px rgba(0,0,0,0.16);
    box-sizing: border-box;
  }

  .tg-tools-claim-icon { color: ${TEAL}; flex-shrink: 0; margin-top: 2px; }

  .tg-tools-claim-title {
    font-size: clamp(18px, 1.6vw, 21px);
    font-weight: 700;
    letter-spacing: -0.025em;
    line-height: 1.25;
    color: ${TEAL};
    margin: 0 0 12px;
  }

  .tg-tools-claim-body {
    font-size: 15px;
    line-height: 1.65;
    letter-spacing: -0.01em;
    color: ${INK};
    margin: 0;
  }

  @media (max-width: 760px) {
    .tg-tools { gap: 22px; }
    .tg-tools-card,
    .tg-tools-claim { padding: 22px 20px; border-radius: 20px; }
    .tg-tools-claim { gap: 14px; }
    .tg-tools-chip { height: 42px; padding: 0 13px; }
    .tg-tools-chip img { height: 20px; }
    .tg-tools-explain { padding: 0; }
  }
`;

const BRANDS = [
  { src: "/images/outils/make.webp",   alt: "Make" },
  { src: "/images/outils/n8n.webp",    alt: "n8n" },
  { src: "/images/outils/zapier.webp", alt: "Zapier" },
];

const KINDS = ["Tableur", "CRM", "IA", "API"];

export default function SystemToolsPanel() {
  return (
    <div className="tg-tools">
      <style>{CSS}</style>

      <div className="tg-tools-card">
        <p className="tg-tools-intro">
          Selon le système, la partie automatisée peut reposer sur
        </p>
        <div className="tg-tools-row">
          {BRANDS.map((b) => (
            <span className="tg-tools-chip" key={b.alt}>
              <img src={b.src} alt={b.alt} loading="lazy" decoding="async" />
            </span>
          ))}
          {KINDS.map((k) => (
            <span className="tg-tools-chip is-kind" key={k}>
              {k}
            </span>
          ))}
        </div>
      </div>

      <p className="tg-tools-explain">
        Ce sont des outils d&apos;automatisation&nbsp;: ils font circuler
        l&apos;information entre vos logiciels, sans que personne ait à recopier quoi que
        ce soit.
      </p>

      <div className="tg-tools-claim">
        <ShieldCheck className="tg-tools-claim-icon" size={38} strokeWidth={1.6} />
        <div>
          <h3 className="tg-tools-claim-title">Les outils évoluent. Le système reste.</h3>
          <p className="tg-tools-claim-body">
            Vous achetez le système, pas les outils. Il tourne sur vos comptes, et si un
            outil change un jour, votre méthode, elle, ne change pas.
          </p>
        </div>
      </div>
    </div>
  );
}
